import React from 'react';
import { Button, Col, Typography, Grid } from 'antd';
import { InfoCircleFilled } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { ErrorCard, Form, ProjectsConstructorForm, useAppFindFormik } from '@/components';
import { CreateSheetDto, ProjectDto } from '@/core';
import { SheetFormInputs } from './SheetFormInputs';
import { SheetFormTables } from './SheetFormTables';
import { useSchemaSheet, FormValueType } from './hooks';
import classes from './SheetForm.module.scss';
import { MainTablesErrors } from './helpers';

const { useBreakpoint } = Grid;

type PropsType = {
  initialValue: FormValueType;
  formUid: string;
  handleCancel?: () => void;
  handleSubmit: (sheetData: CreateSheetDto) => void;
  isCancelButton?: boolean;
  projectData: ProjectDto;
  pickedTables?: string[];
  mainTables: string[];
  summaryTables: string[];
  setPickedTables: (tables: string[]) => void;
  clearForm: () => void;
  stopEditCard?: () => void;
  isEditForm?: boolean;
  editedSheetId?: string;
  mainTableValidationError: string;
};

export const SheetForm = (props: PropsType) => {
  const {
    initialValue,
    formUid,
    handleCancel,
    handleSubmit,
    isCancelButton,
    projectData,
    pickedTables,
    setPickedTables,
    clearForm,
    mainTables,
    summaryTables,
    stopEditCard,
    isEditForm,
    editedSheetId,
    mainTableValidationError,
  } = props;
  const { t } = useTranslation(undefined, { keyPrefix: 'projects.constructor' });
  const form = useAppFindFormik(formUid);
  const schemaSheet = useSchemaSheet(projectData, initialValue);
  const screens = useBreakpoint();
  const isMobileScreen = !screens.sm;

  const handleChangePickedTables = (pickedTables: string[]) => setPickedTables(pickedTables);

  const handleDeleteTable = (table: string) => {
    const filteredTables = pickedTables?.filter((x) => x !== table);
    form.current?.setFieldValue('tables', filteredTables);
    setPickedTables(filteredTables ?? []);
  };

  return (
    <ProjectsConstructorForm<FormValueType>
      handleCancel={handleCancel}
      handleSubmit={handleSubmit}
      schema={schemaSheet}
      initialValues={initialValue}
      formUid={formUid}
      isCancelButton={isCancelButton}
      withoutControls
    >
      <div className={classes.sheetForm}>
        <SheetFormInputs
          projectData={projectData}
          handleChangePickedTables={handleChangePickedTables}
          editedSheetId={editedSheetId}
        />
        {mainTableValidationError && (
          <ErrorCard
            text={
              mainTableValidationError === MainTablesErrors.None
                ? t('addSheets.errorNoMainTables')
                : t('addSheets.errorMultipleMainTables')
            }
          />
        )}
        {pickedTables?.length ? (
          <>
            <div className={classes.informNotification}>
              <InfoCircleFilled className={classes.infoIcon} />
              <Typography.Text>{t('addSheets.rearrange')}</Typography.Text>
            </div>
            <div className={classes.tablesWrapper}>
              <Col span={isMobileScreen ? 24 : 11}>
                <Typography.Title level={4} className={classes.formTitle}>
                  {t('addSheets.mainTables')}
                </Typography.Title>
                {mainTables?.length ? (
                  <SheetFormTables
                    droppableId="mainTables"
                    tables={mainTables}
                    handleDeleteTable={handleDeleteTable}
                    handleDragTable={(tables: string[]) => setPickedTables([...tables, ...summaryTables])}
                    projectData={projectData}
                  />
                ) : (
                  <Typography.Text type="secondary">{t('addSheets.noItems')}</Typography.Text>
                )}
              </Col>
              <Col span={isMobileScreen ? 24 : 11} offset={isMobileScreen ? 0 : 2}>
                <Typography.Title level={4} className={classes.formTitle}>
                  {t('addSheets.summaryTables')}
                </Typography.Title>
                {summaryTables?.length ? (
                  <SheetFormTables
                    droppableId="summaryTables"
                    tables={summaryTables}
                    handleDeleteTable={handleDeleteTable}
                    handleDragTable={(tables: string[]) => setPickedTables([...mainTables, ...tables])}
                    projectData={projectData}
                  />
                ) : (
                  <Typography.Text type="secondary">{t('addSheets.noItems')}</Typography.Text>
                )}
              </Col>
            </div>
          </>
        ) : (
          <>
            <Typography.Title level={4} className={classes.formTitle}>
              {t('addSheets.addedTables')}
            </Typography.Title>
            <Typography.Text type="secondary">{t('addSheets.noItems')}</Typography.Text>
          </>
        )}
        <div className={isEditForm && isMobileScreen ? classes.controls__edit : classes.controls}>
          <Button disabled={!pickedTables?.length} onClick={clearForm} type="default">
            {t('addTables.clear')}
          </Button>
          {isEditForm ? (
            <div className={classes.editFormControls}>
              <Button onClick={stopEditCard}>{t('addTables.cancel')}</Button>
              <Form.Submit type="primary">{t('addTables.save')}</Form.Submit>
            </div>
          ) : (
            <Form.Submit type="primary">{t('addTables.add')}</Form.Submit>
          )}
        </div>
      </div>
    </ProjectsConstructorForm>
  );
};
